import { useEffect } from 'react';

import { isJsonLd, parseGlobalHeaderScripts } from '~/utils/globalHeaderScript';

type Props = {
  script: string;
};

const GlobalHeaderScript = ({ script }: Props) => {
  useEffect(() => {
    const executable = parseGlobalHeaderScripts(script).filter((element) => !isJsonLd(element));
    if (executable.length === 0) return;

    let cancelled = false;
    const injected: HTMLScriptElement[] = [];

    const inject = (index: number) => {
      const element = executable[index];
      if (!element || cancelled) return;

      const node = document.createElement('script');
      for (const [name, value] of Object.entries(element.attributes)) {
        node.setAttribute(name, value);
      }
      node.textContent = element.content;

      const { src, async, defer } = element.attributes;
      const blocking = src !== undefined && async === undefined && defer === undefined;
      if (blocking) {
        const next = () => inject(index + 1);
        node.addEventListener('load', next, { once: true });
        node.addEventListener('error', next, { once: true });
      }

      document.head.appendChild(node);
      injected.push(node);
      if (!blocking) inject(index + 1);
    };

    inject(0);

    return () => {
      cancelled = true;
      for (const node of injected) {
        if (document.head.contains(node)) {
          document.head.removeChild(node);
        }
      }
    };
  }, [script]);

  return (
    <>
      {parseGlobalHeaderScripts(script)
        .filter(isJsonLd)
        .map(({ attributes, content }, index) => (
          <script
            key={attributes.id || `global-header-json-ld-${index}`}
            {...attributes}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ))}
    </>
  );
};

export default GlobalHeaderScript;
